import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import {
  useAddresses,
  useCart,
  useCheckoutPreview,
  useCreateOrder,
  useSaveAddress,
} from "../api/queries.ts";
import type { CheckoutPreview } from "@zx/contracts";

import { api } from "../api/client.ts";
import { usePriceView } from "./identity.ts";

/**
 * 结算页的全部业务逻辑。
 *
 * 🔴 **手机版和网页版是两套版面，但只能有一份逻辑。**
 *    幂等键怎么生成、价格变动怎么重拉、券怎么试算、拆单怎么带 ——
 *    这些抄两份的话迟早走岔，而走岔的地方全是钱。
 *    所以两个页面各写各的 DOM，逻辑统一从这里拿。
 */
/**
 * 「立即下单」买的那一件。
 *
 * 🔴 **它绝不能走购物车。** 用户点「立即下单」的意思是"我只要这一件"——
 *    先加进购物车再结算的话，购物车里上次没买完的东西会**一起被下单**，
 *    而他完全没预期。（下单接口本来就收显式 `items`，所以不用绕。）
 */
export interface BuyNowItem {
  skuId: number;
  quantity: number;
}

/** 结算页上那份还没保存的收货地址。 */
export interface AddressDraft {
  receiver: string;
  phone: string;
  province: string;
  city: string;
  district: string;
  detail: string;
}

/**
 * ⚠️ 手机号在这里只做**最起码**的一道 —— 真正的格式校验在服务端（zod）。
 *    这里拦的是"明显还没填完"，不是"格式对不对"。
 */
const ADDRESS_FIELDS: { key: keyof AddressDraft; label: string }[] = [
  { key: "receiver", label: "收货人" },
  { key: "phone", label: "手机号" },
  { key: "province", label: "省" },
  { key: "city", label: "市" },
  { key: "district", label: "区" },
  { key: "detail", label: "详细地址" },
];

const MISSING = (d: AddressDraft): string[] =>
  ADDRESS_FIELDS.filter((f) => !String(d[f.key] ?? "").trim()).map((f) => f.label);

export function useCheckout(buyNow?: BuyNowItem | null) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const cartQuery = useCart();
  /** 🔴 价格视角要进查询 key —— 换身份看到的是另一个价。 */
  const view = usePriceView();
  const { data: addresses } = useAddresses();
  const saveAddress = useSaveAddress();
  const createOrder = useCreateOrder();

  /**
   * ⚠️ 输码之后要调一次预览试算 —— 减多少只能服务端说了算，
   *    前端自己按面额算的话，遇到"减免不能超过商品额""打折向下取整"
   *    这些规则就会和最终扣款对不上。
   */
  const preview = useCheckoutPreview();
  const [coupon, setCoupon] = useState<CheckoutPreview["coupon"]>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  /** 价格在这次结算过程中变过 —— 提示用户再确认一次。 */
  const [priceChanged, setPriceChanged] = useState(false);

  const [addressId, setAddressId] = useState<number | null>(null);
  /** 提交时地址不全的话，把差哪几项说出来。 */
  const [addressError, setAddressError] = useState<string | null>(null);
  const [note, setNote] = useState("");

  /**
   * ⚠️ **必须是稳定的引用。** 页面把它放进 `useEffect` 的依赖里，
   *    每次渲染都新建一个函数的话那个 effect 每轮都跑
   *    （现在靠 setState 的同值 bailout 兜着，但那是运气不是设计）。
   */
  const clearAddressError = useCallback(() => setAddressError(null), []);

  /**
   * 🔴 **幂等键在进入页面时生成一次，整个页面生命周期内不变。**
   *    每次点击都换一个的话，用户手抖点两下就会下出两组单 ——
   *    后端的幂等靠的就是这个键。
   */
  const idempotencyKey = useMemo(
    () => `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    [],
  );

  const cart = cartQuery.data;
  const chosenAddressId =
    addressId ?? addresses?.find((a) => a.isDefault)?.id ?? addresses?.[0]?.id ?? null;

  /**
   * 🔴 **「立即下单」时，结算页展示的不是购物车，是这一件的试算结果。**
   *    `/checkout/preview` 收的就是显式 `items`，跟购物车走的是同一套算价，
   *    所以金额、拆单、券的规则完全一致 —— 不会出现"两条路两个价"。
   */
  const buyNowPreview = useQuery({
    queryKey: ["checkout", "buy-now", buyNow?.skuId, buyNow?.quantity, view],
    queryFn: () => api.post<CheckoutPreview>("/checkout/preview", { items: [buyNow] }),
    enabled: Boolean(buyNow),
  });

  /** 🔴 视角进 key —— 换身份看到的是另一个价，不进 key 会拿到上一个身份的试算。 */
  const source = buyNow ? buyNowPreview.data : cart;

  const items = buyNow
    ? [buyNow]
    : (cart?.groups.flatMap((g) => g.lines.map((l) => ({ skuId: l.skuId, quantity: l.quantity }))) ??
      []);

  /** 页面上显示的、也是要提交给服务端比对的那个数 —— 只算一次，各处共用。 */
  const payable = (source?.totalPayable ?? 0) - (coupon?.discount ?? 0);
  const blocked = source?.groups.some((g) => g.lines.some((l) => !l.available)) ?? false;

  const applyCoupon = async (code: string) => {
    setCouponError(null);
    const res = await preview.mutateAsync({ items, couponCode: code });
    setCoupon(res.coupon);
    setCouponError(res.couponError);
  };

  const clearCoupon = () => {
    setCoupon(null);
    setCouponError(null);
  };

  /**
   * 🔴 **一个地址都没有时，提交要顺手把页面上填的那份存了。**
   *
   *    以前这里是 `if (!chosenAddressId) return false;` —— 而两个结算页拿到
   *    false 之后都只是 `setShowForm(true)`，**表单本来就是开着的**（没有地址
   *    时它不可能收起来）。于是用户把收货人、手机、省市区、详细地址全填好，
   *    点「提交订单」，页面纹丝不动、一句提示都没有，看着就是按钮坏了。
   *    真机上就是这么卡住的。
   *
   *    正确的做法不是"提示他先点保存" —— 表单就在眼前，那一步对用户毫无意义。
   *    直接存下来再下单。
   *
   * ⚠️ 填不全时要**说清楚差哪一项**，别只说"请填写收货地址"：
   *    他明明填了四格，只差一个区，笼统的提示会让他把填好的又检查一遍。
   */
  const submit = async (draft?: AddressDraft): Promise<boolean> => {
    setAddressError(null);
    let useAddressId = chosenAddressId;

    if (!useAddressId) {
      const missing = draft ? MISSING(draft) : ADDRESS_FIELDS.map((f) => f.label);
      if (missing.length > 0) {
        setAddressError(`收货地址还差：${missing.join("、")}`);
        return false;
      }
      try {
        /**
         * ⚠️ **第一条地址必须是默认的。** 这里必然是"一个地址都没有"的情况
         *    （有地址的话 chosenAddressId 就不是 null），不设默认的话他下次
         *    打开结算页会发现什么都没选中，而这一步没有任何提示。
         *    跟 `/addresses` 那边是同一条规则。
         */
        const saved = await saveAddress.mutateAsync({ ...draft!, isDefault: true });
        useAddressId = saved.id;
        setAddressId(saved.id);
      } catch {
        /** ⚠️ 报错本身由 `saveAddress.error` 渲染，这里只要别继续下单。 */
        return false;
      }
    }
    /**
     * 🔴 **把页面上显示的金额一起提交，让服务端比对。**
     *    价格在这个平台里变动频繁（分销商随时能改加价率、等级升级会改进货价）——
     *    不带这个数的话，用户看着 ¥1000 点提交、真扣 ¥1200 而且毫无提示。
     */
    let res;
    try {
      res = await createOrder.mutateAsync({
        items,
        addressId: useAddressId,
        buyerNote: note || undefined,
        idempotencyKey,
        ...(coupon ? { couponCode: coupon.code } : {}),
        expectedTotal: payable,
      });
    } catch (err) {
      /**
       * 🔴 价格变了不能只甩一句错误就完 —— 要**把新价拉回来重新显示**，
       *    否则用户看到"价格已变动"却还是原来那个数，只会反复点提交。
       */
      if ((err as { code?: string })?.code === "PRICE_CHANGED") {
        setPriceChanged(true);
        /**
         * ⚠️ **要作废的是页面正在显示的那一条查询。**
         *    「立即下单」时屏幕上的金额来自 `["checkout","buy-now",…]`，
         *    只作废购物车那份的话，提示写着"价格已变动"、数字还是旧的 ——
         *    正是这句提示要防的那种现象。
         */
        await qc.invalidateQueries({ queryKey: buyNow ? ["checkout", "buy-now"] : ["cart"] });
        if (coupon) {
          const fresh = await preview.mutateAsync({ items, couponCode: coupon.code });
          setCoupon(fresh.coupon);
          setCouponError(fresh.couponError);
        }
      }
      return false;
    }
    /**
     * 🔴 下完单直接去收银台 —— 一次结算拆出来的几张单是**一起付**的，
     *    把订单 id 全带过去，收款单按它们的合计建。
     */
    navigate({ to: "/pay", search: { orderIds: res.orders.map((o) => o.id).join(",") } });
    return true;
  };

  return {
    /** ⚠️ 「立即下单」时 loading/错误看的是那次试算，不是购物车查询。 */
    cartQuery: buyNow ? buyNowPreview : cartQuery,
    /** 页面照旧读 `cart` 渲染 —— 立即下单时它是这一件的试算结果。 */
    cart: source,
    addresses,
    saveAddress,
    createOrder,
    preview,
    coupon,
    couponError,
    applyCoupon,
    clearCoupon,
    priceChanged,
    /** 提交时地址填不全 —— 页面把它显示在提交按钮旁边。 */
    addressError,
    /**
     * ⚠️ 用户一动表单就把这条提示清掉。
     *    不清的话他补完收货人，旁边还写着"还差：收货人、手机号…"——
     *    比不提示更让人困惑，他会以为自己填的没被认到。
     */
    clearAddressError,
    addressId: chosenAddressId,
    setAddressId,
    note,
    setNote,
    items,
    payable,
    blocked,
    submit,
  };
}
